/**
 * Admin Alert RPC definitions
 *
 * Admin endpoints for surfacing node health alerts - requires admin role.
 * Alerts are computed from existing node data and Valkey health status.
 *
 * Alert types:
 * - `node_offline`: Node was online but Valkey last_seen TTL expired (>5 min)
 * - `stuck_provisioning`: Node status is "provisioning" with no update for N hours
 * - `gateway_unreachable`: No heartbeat data in Valkey for an active node
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized, ValkeyError } from "../errors.ts";

// ============================================================================
// Schemas
// ============================================================================

/**
 * Alert severity levels.
 *
 * - `critical`: Immediate attention required (e.g. node offline)
 * - `warning`: Should be investigated soon (e.g. stuck provisioning)
 * - `info`: Informational notice
 *
 * @since 1.0.0
 */
export const AlertSeverity = Schema.Literal("critical", "warning", "info");
export type AlertSeverity = Schema.Schema.Type<typeof AlertSeverity>;

/**
 * Alert type identifiers.
 *
 * - `node_offline`: Valkey last_seen expired (was online, now offline)
 * - `stuck_provisioning`: Node in provisioning status for too long
 * - `gateway_unreachable`: No heartbeat data at all for an active node
 *
 * @since 1.0.0
 */
export const AlertType = Schema.Literal("node_offline", "stuck_provisioning", "gateway_unreachable");
export type AlertType = Schema.Schema.Type<typeof AlertType>;

/**
 * A single node alert.
 * @since 1.0.0
 */
export const NodeAlert = Schema.Struct({
  type: AlertType,
  severity: AlertSeverity,
  nodeId: Schema.String,
  nodeName: Schema.String,
  /** Owning customer's name, or `null` when the installation has no customer. */
  customerName: Schema.NullOr(Schema.String),
  message: Schema.String,
  detectedAt: Schema.String,
});
export type NodeAlert = Schema.Schema.Type<typeof NodeAlert>;

/**
 * Default threshold in milliseconds for "stuck provisioning" alerts (2 hours).
 * @since 1.0.0
 */
export const STUCK_PROVISIONING_THRESHOLD_MS = 2 * 60 * 60 * 1000;

// ============================================================================
// Response Schema
// ============================================================================

/**
 * Response for the alerts list endpoint.
 * @since 1.0.0
 */
export const AlertListResponse = Schema.Struct({
  items: Schema.Array(NodeAlert),
  total: Schema.Number,
});
export type AlertListResponse = Schema.Schema.Type<typeof AlertListResponse>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Alert RPC group
 *
 * Provides an endpoint to list current node health alerts.
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminAlertRpcs extends RpcGroup.make(
  Rpc.make("admin.alerts.findMany", {
    success: AlertListResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ValkeyError),
  }).middleware(AdminMiddleware),
) {}
